import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { useToast } from 'components/hooks/useToast';
import { E_SIGN_PDF_STATUS } from 'screens/performanceReview/ResultPage/RabbitResultDocusignAcknowledge';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import type { ViolationNote } from './types';

type EvaluationStatus = 'APPROVED' | 'DENIED';

type BulkEvaluateNotesParams = {
  noteIds: number[];
  evaluation: EvaluationStatus;
  reason: string;
};

type EvaluateNoteArgs = {
  noteId: number;
  body: {
    evaluation: EvaluationStatus;
    reason?: string;
  };
};

const getViolationNote = async (noteId: number): Promise<ViolationNote> => {
  try {
    const url = endpoints.disciplinaryActions.getViolationNote(noteId);
    const response = await sendRequest({ url, credentials: 'include' });
    return response;
  } catch (error) {
    console.error('Error fetching violation note:', error);
    throw error;
  }
};

export const useGetViolationNote = ({ noteId, enabled }: { noteId: number; enabled: boolean }) =>
  useQuery<ViolationNote>({
    queryKey: [REACT_QUERY_KEYS.disciplinaryActions.getViolationNote, noteId],
    queryFn: () => getViolationNote(noteId),
    enabled: !!noteId && enabled,
  });

const evaluateNote = async ({ noteId, body }: EvaluateNoteArgs) => {
  try {
    const url = endpoints.disciplinaryActions.evaluateNote(noteId);
    const response = await sendRequest({ url, method: 'PATCH', credentials: 'include', body });
    return response;
  } catch (error) {
    console.error('Error evaluating note:', error);
    throw error;
  }
};

export const useEvaluateNote = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: evaluateNote,
    onSuccess: (_, variables) => {
      const queryKeysToInvalidate = [
        [REACT_QUERY_KEYS.disciplinaryActions.getViolationNote, variables.noteId],
        [REACT_QUERY_KEYS.disciplinaryActions.getDisciplinaryActions],
        [REACT_QUERY_KEYS.disciplinaryActions.getDisciplinaryActionsNotesStageChartData],
        [REACT_QUERY_KEYS.disciplinaryActions.getDisciplinaryActionsNotesCountChartData],
      ];

      queryKeysToInvalidate.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
      toast({
        status: 'success',
        title: `Disciplinary action ${variables.body.evaluation.toLowerCase()} successfully`,
      });
    },
    onError: (error, variables) => {
      console.error('Error evaluating note', error, variables);
      toast({ status: 'error', title: 'Error evaluating note' });
    },
  });
};

const bulkEvaluateNotes = async ({ noteIds, evaluation, reason }: BulkEvaluateNotesParams) => {
  try {
    const url = endpoints.disciplinaryActions.bulkEvaluateDisciplinaryNotes;

    const response = await sendRequest({
      url,
      method: 'PATCH',
      credentials: 'include',
      body: {
        noteIds,
        evaluation,
        reason,
      },
    });
    return response;
  } catch (error) {
    console.error('Error bulk evaluating notes:', error);
    throw error;
  }
};

export const useBulkEvaluateNotes = () =>
  useMutation({
    mutationFn: (params: BulkEvaluateNotesParams) => bulkEvaluateNotes(params),
  });

const acknowledgeNote = async (noteId: number) => {
  try {
    const url = endpoints.disciplinaryActions.acknowledgeNote(noteId);
    const response = await sendRequest({ url, method: 'PATCH', credentials: 'include' });
    return response;
  } catch (error) {
    console.error('Error acknowledging note:', error);
    throw error;
  }
};

export const uploadPdf = async ({ noteId, formData }: { noteId: number; formData: FormData }): Promise<void> => {
  try {
    const url = endpoints.disciplinaryActions.uploadUnsignedPdf(noteId);
    await sendRequest({ url, method: 'POST', credentials: 'include', body: formData, isMultiPart: true });
  } catch (error) {
    console.error('Error uploading PDF:', error);
    throw error;
  }
};

export const useAcknowledgeNote = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (noteId: number) => acknowledgeNote(noteId),
    onSuccess: (_, noteId) => {
      const queryKeysToInvalidate = [
        [REACT_QUERY_KEYS.disciplinaryActions.getViolationNote, noteId],
        [REACT_QUERY_KEYS.disciplinaryActions.getDisciplinaryActions],
        [REACT_QUERY_KEYS.disciplinaryActions.getDisciplinaryActionsNotesStageChartData],
        [REACT_QUERY_KEYS.disciplinaryActions.getDisciplinaryActionsNotesCountChartData],
      ];
      queryKeysToInvalidate.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
      toast({
        status: 'success',
        title: 'Note acknowledged successfully',
      });
    },
    onError: (error) => {
      console.error('Error acknowledging note', error);
      toast({ status: 'error', title: 'Error acknowledging note' });
    },
  });
};

const getAcknowledgePollApi = async ({ noteId }) => {
  try {
    const url = endpoints.disciplinaryActions.getAcknowledgePoll(noteId);
    const response = await sendRequest({ url, credentials: 'include' });
    return response;
  } catch (error) {
    console.error('Error fetching acknowledge poll:', error);
    throw error;
  }
};

export const useAcknowledgeNotePoll = ({ noteId, enabled }) =>
  useQuery(
    [REACT_QUERY_KEYS.disciplinaryActions.pollAcknowledge, noteId],
    () =>
      getAcknowledgePollApi({
        noteId,
      }).then((res) => res),

    {
      enabled,
      retry: false,
      refetchInterval: (data) => {
        if (data?.status === E_SIGN_PDF_STATUS.STARTED) {
          // try again after 5 sec
          return 5000;
        }
        return false;
      },
    }
  );
